import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from './useAccessToken';
import { useMemo } from 'react';
import { useLogger } from '../context/useLogger';
import { AccountRole } from '../types/profiles.types';
import {
  EdiAdminClientProfilesService,
  OAuthClientRole,
} from '../services/EdiAdminClientProfilesService';

const ROLES_QUERY_KEY = ['roles'] as const;

const sortRoles = (roles: AccountRole[]): AccountRole[] =>
  roles
    .slice()
    .sort(
      (roleA, roleB) =>
        roleA.DisplayOrder - roleB.DisplayOrder || roleA.Label.localeCompare(roleB.Label),
    );

const normalizeRole = (role: OAuthClientRole): AccountRole => ({
  InstanceId: role.InstanceId,
  Label: role.Label ?? '< Non défini >',
  DisplayOrder: role.DisplayOrder ?? Number.MAX_SAFE_INTEGER,
});

export const useRoles = () => {
  const getAccessToken = useAccessToken();
  const logger = useLogger();

  const backendService = useMemo(
    () => new EdiAdminClientProfilesService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const {
    data: roles = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: async () => {
      const data = await backendService.fetchGetClientRoles();
      return sortRoles(data.map(normalizeRole));
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (les rôles changent rarement)
    retry: 2,
  });

  return {
    roles,
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetchRoles: refetch,
  };
};
